import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Users, UserPlus, Edit, Trash2, Search, UserX, X, Receipt, ChevronRight, ChevronDown, CornerDownRight, Landmark } from 'lucide-react';
import CustomerInvoices from './CustomerInvoices';
import CreditorMaster from '../Accounts/CreditorMaster';
import LedgerButton from '../Accounts/LedgerButton';
import { listCreditors } from '../Accounts/reconApi';
import { PageHead, Panel, Pill, btn, Modal, FormField, ConfirmModal, field, Select } from './AdminUI';
import toast from 'react-hot-toast';
import { apiCall, API_ENDPOINTS } from '../../config/api';

/* A client record carries either `userId` (the customer's own billing identity)
   or `usedById` (a client that sits under that customer), or neither — which is
   exactly the three tabs below. The API answers in both camelCase and
   snake_case depending on the route, so every read goes through here. */
const ownerIdOf = (r) => r?.userId ?? r?.user_id ?? null;
const usedByIdOf = (r) => r?.usedById ?? r?.used_by_id ?? null;

const TABS = [
  { id: 'clients',  label: 'Customers',     icon: Users,   hint: 'Open a customer to see the clients that sit under them' },
  { id: 'misc',     label: 'Miscellaneous', icon: UserX,   hint: 'Billing details not linked to any customer' },
  { id: 'creditors', label: 'Creditors', icon: Landmark, hint: 'The carriers and agents that bill us — a purchase bill is read against one of these' },
  { id: 'invoices', label: 'Customer Invoices', icon: Receipt, hint: 'Invoices raised against a client, with no shipment attached' },
];

// A record either hangs off a customer (own billing identity or one of their
// clients) or off nobody at all.
const tabOf = (r) => (ownerIdOf(r) || usedByIdOf(r) ? 'clients' : 'misc');

const toArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (payload?.data) return [payload.data];
  return [];
};

const readJson = (promise) => promise.then((res) => (res.ok ? res.json() : null)).catch(() => null);

const RowActions = ({ row, onEdit, onDelete }) => (
  <div className="flex gap-2">
    <button
      onClick={() => onEdit(row)}
      className="p-1.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800 transition"
      title="Edit Client Details"
    >
      <Edit size={16} />
    </button>
    <button
      onClick={() => onDelete(row)}
      className="p-1.5 rounded bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-800 transition"
      title="Delete Client Details"
    >
      <Trash2 size={16} />
    </button>
  </div>
);

/**
 * Client Master — the billing parties invoices are raised against.
 * Shared by the admin, super admin and accountant dashboards.
 */
const ClientMaster = ({ role = 'admin', customers = [], onEditInvoice, search: searchProp, onSearch }) => {
  const [clients, setClients] = useState([]);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('clients');
  // The tab carries a number before it is ever opened, so count them up front
  // rather than waiting for CreditorMaster to mount and report back.
  const [creditorCount, setCreditorCount] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  // The console's topbar can drive this instead, so the page's own box and
  // the one up there are the same search rather than two that disagree.
  const [ownSearch, setOwnSearch] = useState('');
  const controlled = typeof searchProp === 'string';
  const search = controlled ? searchProp : ownSearch;
  const setSearch = controlled ? (onSearch || (() => {})) : setOwnSearch;

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selected, setSelected] = useState(null);
  const [newClient, setNewClient] = useState({ name: '', address: '', gstin: '', userId: '' });
  const [editClient, setEditClient] = useState({ name: '', address: '', gstin: '' });
  const [saving, setSaving] = useState(false);

  const customerById = useMemo(() => {
    const map = new Map();
    customers.forEach((c) => map.set(c.id, c));
    return map;
  }, [customers]);

  const nameOfUser = useCallback((included, id) => (
    included?.Name || included?.name || customerById.get(id)?.name || (id ? `User #${id}` : '—')
  ), [customerById]);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const collected = new Map();
      const absorb = (rows) => rows.forEach((r) => {
        if (r && r.id != null && !collected.has(r.id)) collected.set(r.id, r);
      });

      // One call covers what the role is allowed to see: everything for a super
      // admin, and for the others the records that belong to nobody.
      const all = await readJson(apiCall(API_ENDPOINTS.INVOICE_GET_CUSTOMERS, { method: 'GET' }));
      if (all) absorb(toArray(all));

      // Admin and accountant walk their own customers as well — /invoices/user/:id
      // matches user_id or used_by_id, so it brings back both of the first two
      // tabs. A super admin only needs this if the list call was refused.
      if (customers.length && (role !== 'super_admin' || !all)) {
        const responses = await Promise.all(customers.map((c) =>
          readJson(apiCall(`${API_ENDPOINTS.INVOICE_GET_CUSTOMERS}/user/${c.id}`, { method: 'GET' }))
        ));
        responses.forEach((res) => { if (res) absorb(toArray(res)); });
      }

      setClients([...collected.values()]);
    } catch (error) {
      console.error('Error fetching client master:', error);
      toast.error('Failed to load client master');
    } finally {
      setLoading(false);
    }
  }, [customers, role]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  // Invoices billed straight to a client — they carry no AWB, so the shipment
  // tables never list them.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await readJson(apiCall(`${API_ENDPOINTS.INVOICE_SUBMIT}?invoice_type=customer`, { method: 'GET' }));
      if (!cancelled && res) setCustomerInvoices(toArray(res));
    })();
    return () => { cancelled = true; };
  }, []);

  // One row per customer, with that customer's own billing identity on the row
  // and their clients nested underneath it.
  const groups = useMemo(() => {
    const map = new Map();
    const ensure = (id, name) => {
      if (!map.has(id)) map.set(id, { id, account: name, own: null, subs: [] });
      const g = map.get(id);
      if (name && !String(name).startsWith('User #')) g.account = name;
      return g;
    };

    clients.forEach((c) => {
      const ownerId = ownerIdOf(c);
      const usedById = usedByIdOf(c);
      if (ownerId) {
        const g = ensure(ownerId, nameOfUser(c.user, ownerId));
        // A customer should hold one own identity; anything beyond it reads as
        // another of their clients rather than being dropped.
        if (g.own) g.subs.push(c); else g.own = c;
      } else if (usedById) {
        ensure(usedById, nameOfUser(c.usedByUser, usedById)).subs.push(c);
      }
    });

    return [...map.values()].sort((a, b) => String(a.account).localeCompare(String(b.account)));
  }, [clients, nameOfUser]);

  const misc = useMemo(() => clients.filter((c) => tabOf(c) === 'misc'), [clients]);

  const matches = useCallback((row, term) => row && [row.name, row.gstin, row.address]
    .some((v) => String(v || '').toLowerCase().includes(term)), []);

  // Searching keeps a customer whose own details or account name match, with all
  // their clients; otherwise only the clients that match.
  const visibleGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return groups;
    return groups.reduce((out, g) => {
      if (String(g.account).toLowerCase().includes(term) || matches(g.own, term)) out.push(g);
      else {
        const subs = g.subs.filter((c) => matches(c, term));
        if (subs.length) out.push({ ...g, subs });
      }
      return out;
    }, []);
  }, [groups, search, matches]);

  const visibleMisc = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term ? misc.filter((c) => matches(c, term)) : misc;
  }, [misc, search, matches]);

  useEffect(() => {
    let alive = true;
    listCreditors().then((l) => { if (alive) setCreditorCount(l.length); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const counts = { clients: groups.length, misc: misc.length, creditors: creditorCount ?? '·', invoices: customerInvoices.length };
  const searching = search.trim().length > 0;
  const isOpen = (id) => searching || expanded.has(id);
  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const activeTab = TABS.find((t) => t.id === tab);

  const openEdit = (row) => {
    setSelected(row);
    setEditClient({ name: row.name || '', address: row.address || '', gstin: row.gstin || '' });
    setShowEdit(true);
  };

  const openDelete = (row) => { setSelected(row); setShowDelete(true); };

  const handleAdd = async (e) => {
    e.preventDefault();
    const selectedUserId = parseInt(newClient.userId, 10);
    if (!selectedUserId) { toast.error('Select a customer first'); return; }

    // The first record for a customer is their own billing identity (userId);
    // anything after that is one of their clients (usedById).
    const alreadyHasOwn = clients.some((c) => ownerIdOf(c) === selectedUserId);
    const body = {
      [alreadyHasOwn ? 'usedById' : 'userId']: selectedUserId,
      name: newClient.name,
      address: newClient.address,
      gstin: newClient.gstin,
    };

    setSaving(true);
    try {
      const response = await apiCall(API_ENDPOINTS.INVOICE_ADD_CUSTOMER, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success('Client details added successfully');
        const saved = data.data || {};
        setClients((prev) => [saved, ...prev]);
        setTab(tabOf(saved));
        const owner = ownerIdOf(saved) || usedByIdOf(saved);
        if (owner) setExpanded((prev) => new Set(prev).add(owner));
        setShowAdd(false);
        setNewClient({ name: '', address: '', gstin: '', userId: '' });
      } else {
        toast.error(data.message || 'Failed to add client details');
      }
    } catch (error) {
      console.error('Error adding client:', error);
      toast.error('Failed to add client details');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSaving(true);
    try {
      const response = await apiCall(`${API_ENDPOINTS.INVOICE_GET_CUSTOMERS}/${selected.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: editClient.name,
          address: editClient.address,
          gstin: editClient.gstin,
        }),
      });
      const data = await response.json();
      if (response.ok && data.success !== false) {
        toast.success('Client details updated successfully');
        setClients((prev) => prev.map((c) => (c.id === selected.id ? { ...c, ...editClient } : c)));
        setShowEdit(false);
        setSelected(null);
      } else {
        toast.error(data.message || 'Failed to update client details');
      }
    } catch (error) {
      console.error('Error updating client:', error);
      toast.error('Failed to update client details');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const response = await apiCall(`${API_ENDPOINTS.INVOICE_DELETE_CUSTOMER}/${selected.id}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        toast.success('Client details deleted successfully');
        setClients((prev) => prev.filter((c) => c.id !== selected.id));
        setShowDelete(false);
        setSelected(null);
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.message || 'Failed to delete client details');
      }
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error('Failed to delete client details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHead
        eyebrow="Customs & finance hub · Billing parties"
        title="Client Master"
        badge={<Pill tone="blue">{clients.length} clients</Pill>}
        subtitle={activeTab?.hint}
        actions={tab !== 'invoices' && tab !== 'creditors' && (
          <button type="button" className={btn.primary}
            onClick={() => { setNewClient({ name: '', address: '', gstin: '', userId: '' }); setShowAdd(true); }}>
            <UserPlus size={16} /> Add client
          </button>
        )}
      />

      <Panel className="flex flex-wrap items-center gap-2 px-4 sm:px-5 py-4 mb-5">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition border ${
                isActive
                  ? 'bg-blue-600 text-white border-transparent shadow-sm'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Icon size={16} />
              {t.label}
              <span className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-bold ${
                isActive ? 'bg-white/20 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
              }`}>
                {counts[t.id]}
              </span>
            </button>
          );
        })}

        {/* The console's topbar carries the search; only a standalone use of
            this page draws one of its own. */}
        {!controlled && (
          <div className="relative sm:ml-auto w-full sm:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tab === 'invoices' ? "Search invoice no, client, GST…" : "Search name, GST, address…"}
              className="w-full pl-9 pr-8 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                title="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        )}
      </Panel>

      {/* Table */}
      {tab === 'creditors' ? (
        <CreditorMaster embedded search={search} onCount={setCreditorCount} />
      ) : tab === 'invoices' ? (
        <CustomerInvoices
          rows={customerInvoices}
          search={search}
          onEdit={(row) => onEditInvoice?.(row, customerInvoices)}
          onDeleted={(id) => setCustomerInvoices((prev) => prev.filter((r) => r.id !== id))}
        />
      ) : loading ? (
        <Panel className="py-20 grid place-items-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </Panel>
      ) : (
        <Panel className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800">
                  {['Name', 'Address', 'GST No', 'Actions'].map((h) => (
                    <th key={h} className="px-4 sm:px-5 py-3 text-left text-[10.5px] font-black uppercase tracking-[0.1em]
                                           text-slate-400 dark:text-slate-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
                {tab === 'clients' && visibleGroups.map((g) => {
                  const open = isOpen(g.id);
                  return (
                    <React.Fragment key={g.id}>
                      {/* the customer */}
                      <tr
                        className={`${g.subs.length ? 'cursor-pointer' : ''} hover:bg-gray-50 dark:hover:bg-gray-700`}
                        onClick={g.subs.length ? () => toggle(g.id) : undefined}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-start gap-2">
                            <span className={`mt-0.5 flex-shrink-0 w-5 h-5 grid place-items-center rounded ${
                              g.subs.length
                                ? 'text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700'
                                : 'text-transparent'
                            }`}>
                              {g.subs.length ? (open ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <ChevronRight size={14} />}
                            </span>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                {g.own?.name || g.account}
                                {!g.own && (
                                  <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400">no billing details yet</span>
                                )}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {g.account}
                                {g.subs.length > 0 && (
                                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 text-[10px] font-bold">
                                    {g.subs.length} client{g.subs.length > 1 ? 's' : ''}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate" title={g.own?.address}>{g.own?.address || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{g.own?.gstin || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-2">
                            <LedgerButton id={g.id} name={g.own?.name || g.account} />
                            {g.own ? <RowActions row={g.own} onEdit={openEdit} onDelete={openDelete} /> : null}
                          </div>
                        </td>
                      </tr>

                      {/* the clients under them */}
                      {open && g.subs.map((c) => (
                        <tr key={c.id} className="bg-slate-50 dark:bg-gray-900/40 hover:bg-slate-100 dark:hover:bg-gray-700">
                          <td className="px-6 py-3">
                            <div className="flex items-start gap-2 ml-2.5 pl-4 border-l-2 border-blue-200 dark:border-blue-900/70">
                              <CornerDownRight size={14} className="mt-0.5 flex-shrink-0 text-blue-400 dark:text-blue-500" />
                              <div className="min-w-0">
                                <p className="text-sm text-gray-900 dark:text-white">{c.name || '-'}</p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">client of {g.account}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate" title={c.address}>{c.address || '-'}</td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{c.gstin || '-'}</td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm">
                            <RowActions row={c} onEdit={openEdit} onDelete={openDelete} />
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}

                {tab === 'misc' && visibleMisc.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{c.name || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate" title={c.address}>{c.address || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{c.gstin || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <RowActions row={c} onEdit={openEdit} onDelete={openDelete} />
                    </td>
                  </tr>
                ))}

                {((tab === 'clients' && visibleGroups.length === 0) || (tab === 'misc' && visibleMisc.length === 0)) && (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      {search ? 'No client matches this search' : 'No client details found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {/* Add */}
      <Modal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        icon={UserPlus}
        tone="blue"
        title="Add client details"
        subtitle="The billing party an invoice is raised against."
        footer={(
          <div className="flex flex-col-reverse sm:flex-row gap-2.5">
            <button type="button" className={btn.quiet} onClick={() => setShowAdd(false)}>Cancel</button>
            <button type="submit" form="client-add" disabled={saving}
              className={`${btn.primary} flex-1 justify-center disabled:opacity-60`}>
              {saving ? 'Saving…' : 'Add client'}
            </button>
          </div>
        )}
      >
        <form id="client-add" onSubmit={handleAdd} className="space-y-4">
          <FormField label="Customer" required>
            <Select
              clearable={false}
              value={newClient.userId}
              onChange={(v) => setNewClient({ ...newClient, userId: v })}
              options={customers.map((c) => ({ value: String(c.id), label: c.email ? `${c.name} (${c.email})` : c.name }))}
              allLabel="Select a customer"
            />
          </FormField>

          {newClient.userId && (
            clients.some((c) => ownerIdOf(c) === parseInt(newClient.userId, 10)) ? (
              <div className="px-3.5 py-2.5 rounded-xl text-[12.5px] font-semibold
                              border border-emerald-200 bg-emerald-50/70 text-emerald-700
                              dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300">
                This customer already has billing details — the new record is saved as one of their clients.
              </div>
            ) : (
              <div className="px-3.5 py-2.5 rounded-xl text-[12.5px] font-semibold
                              border border-amber-200 bg-amber-50/70 text-amber-700
                              dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
                This customer has no billing details yet — this becomes their own billing identity.
              </div>
            )
          )}

          <FormField label="Name" required>
            <input type="text" value={newClient.name} placeholder="Company name" required
              onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} className={field} />
          </FormField>

          <FormField label="Address" required>
            <textarea value={newClient.address} rows={3} placeholder="Business address" required
              onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
              className={`${field} h-auto py-2.5 resize-none`} />
          </FormField>

          <FormField label="GST No" required>
            <input type="text" value={newClient.gstin} placeholder="22AAAAA0000A1Z5" required
              onChange={(e) => setNewClient({ ...newClient, gstin: e.target.value.toUpperCase() })}
              className={`${field} font-mono`} />
          </FormField>
        </form>
      </Modal>

      {/* Edit */}
      <Modal
        open={showEdit}
        onClose={() => { setShowEdit(false); setSelected(null); }}
        icon={Edit}
        tone="blue"
        title="Edit client details"
        subtitle={selected?.name}
        footer={(
          <div className="flex flex-col-reverse sm:flex-row gap-2.5">
            <button type="button" className={btn.quiet} onClick={() => { setShowEdit(false); setSelected(null); }}>Cancel</button>
            <button type="submit" form="client-edit" disabled={saving}
              className={`${btn.primary} flex-1 justify-center disabled:opacity-60`}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        )}
      >
        <form id="client-edit" onSubmit={handleUpdate} className="space-y-4">
          <div className="px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-50
                          dark:border-slate-800 dark:bg-slate-900/50">
            <p className="text-[9.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Belongs to</p>
            <p className="text-[13px] font-bold text-slate-900 dark:text-white mt-0.5">
              {ownerIdOf(selected)
                ? nameOfUser(selected?.user, ownerIdOf(selected))
                : usedByIdOf(selected)
                  ? nameOfUser(selected?.usedByUser, usedByIdOf(selected))
                  : 'Not assigned to any customer'}
            </p>
          </div>

          <FormField label="Name" required>
            <input type="text" value={editClient.name} required
              onChange={(e) => setEditClient({ ...editClient, name: e.target.value })} className={field} />
          </FormField>

          <FormField label="Address" required>
            <textarea value={editClient.address} rows={3} required
              onChange={(e) => setEditClient({ ...editClient, address: e.target.value })}
              className={`${field} h-auto py-2.5 resize-none`} />
          </FormField>

          <FormField label="GST No" required>
            <input type="text" value={editClient.gstin} required
              onChange={(e) => setEditClient({ ...editClient, gstin: e.target.value.toUpperCase() })}
              className={`${field} font-mono`} />
          </FormField>
        </form>
      </Modal>

      {/* Delete */}
      {showDelete && (
        <ConfirmModal
          open
          busy={saving}
          onClose={() => { setShowDelete(false); setSelected(null); }}
          onConfirm={handleDelete}
          title="Delete client details"
          subtitle={selected?.gstin ? `GST ${selected.gstin}` : undefined}
          confirmLabel="Delete client"
        >
          <p className="text-[13px] text-slate-600 dark:text-slate-300">
            <span className="font-bold text-slate-900 dark:text-white">{selected?.name}</span> is removed from the
            client master. Invoices already raised against it keep the details they were issued with.
          </p>
        </ConfirmModal>
      )}
    </div>
  );
};

export default ClientMaster;
